import { useCallback, useEffect, useSyncExternalStore } from 'react'

import { Box } from '@chakra-ui/react'
import { useSendVideoCallStartedNotification } from 'src/features/explanation/api/sendVideoCallStartedNotification'

import { useThisSession } from './useThisSession'
import { FloatingVideoPlayer } from '../components/FloatingVideoPlayer/FloatingVideoPlayer'
import { useRoomMediaContext } from '../context/MediaProvider'

export const useFloatingVideoCallForPatient = ({
  sessionUid,
}: {
  sessionUid: string
}) => {
  const { initializePatientVideoCall } = useRoomMediaContext()

  const thisSession = useThisSession({ sessionUid, forPatient: true })

  const { request: sendVideoCallStartedNotification } =
    useSendVideoCallStartedNotification()

  const sendNotification = useCallback(() => {
    if (!thisSession.fetched) return
    sendVideoCallStartedNotification({
      trialPatientUid: thisSession.patient.trialPatientUid,
      scene: 'Explanation',
      sendTo: 'Patient',
    })
  }, [thisSession, sendVideoCallStartedNotification])

  useEffect(() => {
    if (!thisSession.fetched) return
    if (!thisSession.videoCallRoomUid) return

    initializePatientVideoCall({
      roomUid: thisSession.videoCallRoomUid,
      candidateId: thisSession.patient.candidateId,
    })
  }, [thisSession, initializePatientVideoCall])

  const subscribe = useCallback((onStoreChange: () => void) => {
    const observer = new ResizeObserver(entries => {
      entries.forEach(() => onStoreChange())
    })
    observer.observe(document.body)
    return () => observer.disconnect()
  }, [])

  const bodyHeight = useSyncExternalStore(subscribe, () => {
    return document.body.clientHeight
  })
  const bodyWidth = useSyncExternalStore(subscribe, () => {
    return document.body.clientWidth
  })

  const renderFloatingVideoCall = useCallback(
    (position?: { right: number; bottom: number }) => {
      const right = position?.right ?? 24
      const bottom = position?.bottom ?? 24
      const maxHeightPx = bodyHeight - (bottom + 24)
      const maxWidthPx = bodyWidth - (right + 24)
      return (
        <Box
          position="absolute"
          right={`${right}px`}
          bottom={`${bottom}px`}
          zIndex="docked" // PDFビューアーの要素より上に表示する
        >
          <FloatingVideoPlayer
            maxHeightPx={maxHeightPx}
            maxWidthPx={maxWidthPx}
            canShareScreen={false}
            sendNotification={sendNotification}
          />
        </Box>
      )
    },
    [bodyHeight, bodyWidth, sendNotification],
  )

  return {
    renderFloatingVideoCall,
  }
}
